import { useEffect, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Spinner } from "@/components/ui/spinner";

export const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 8,
};

export const coverStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

export interface TileProps {
  dimmed: boolean;
  inert?: boolean;
  hoverIcon: "check" | "x" | "plus";
  title: string;
  badge?: string;
  sortable?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  innerRef?: (node: HTMLElement | null) => void;
  extraStyle?: React.CSSProperties;
  handleProps?: Record<string, unknown>;
}

export function Tile({
  dimmed,
  inert,
  hoverIcon,
  title,
  badge,
  sortable,
  onClick,
  children,
  innerRef,
  extraStyle,
  handleProps,
}: TileProps) {
  const [hovered, setHovered] = useState(false);
  const Icon = hoverIcon === "check" ? Check : hoverIcon === "x" ? X : Plus;

  return (
    <button
      type="button"
      title={title}
      ref={innerRef}
      {...handleProps}
      onClick={() => {
        if (!inert) onClick();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        aspectRatio: "1",
        overflow: "hidden",
        border: "1px solid var(--line)",
        background: "var(--surface-2)",
        padding: 0,
        cursor: inert ? "default" : sortable ? "grab" : "pointer",
        opacity: dimmed ? 0.4 : 1,
        touchAction: sortable ? "none" : undefined,
        ...extraStyle,
      }}
    >
      {children}
      {badge && (
        <span
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            padding: "2px 6px",
            fontFamily: "var(--f-mono)",
            fontSize: 9,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            background: "var(--ink)",
            color: "var(--surface)",
          }}
        >
          {badge}
        </span>
      )}
      {hovered && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            background: "rgba(0,0,0,0.45)",
            color: "#fff",
          }}
        >
          <Icon size={22} strokeWidth={2.5} />
        </span>
      )}
    </button>
  );
}

export function SortableTile({ id, ...tile }: TileProps & { id: string }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Tile
      {...tile}
      sortable
      innerRef={setNodeRef}
      handleProps={{ ...attributes, ...listeners }}
      extraStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined,
        boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.35)" : undefined,
        cursor: isDragging ? "grabbing" : "grab",
      }}
    />
  );
}

export function RemoteThumbnail({
  load,
  alt,
}: {
  load: () => Promise<Blob>;
  alt: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    load()
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return (
      <div
        style={{
          display: "grid",
          placeItems: "center",
          width: "100%",
          height: "100%",
          fontSize: 11,
          color: "var(--ink-soft)",
        }}
      >
        No preview
      </div>
    );
  }

  if (!url) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100%" }}>
        <Spinner />
      </div>
    );
  }

  return <img src={url} alt={alt} style={coverStyle} />;
}
